import classNames from "classnames";
import React, { FC, memo, useContext } from "react";
import { DefaultPropsContext } from "../../contexts/default-props-context-provider";
import { PaletteColorOption } from "../types";

export type BorderWidthOption = "0" | "1" | "2" | "3";

export type RoundedOption = "none" | "xs" | "sm" | "md" | "lg" | "xl";

export type PaddingOption = "none" | "xs" | "sm" | "md" | "lg" | "xl";

export interface PanelProps {
  /**
   * A unique `id` for rendered element.
   */
  id?: string;

  /**
   * Controls the additional classNames passed to the component styling
   * This cam be a string containing multiple classes space separated
   */
  className?: string;
  /**
   * Controls panel border width.
   * @default 1
   */
  border?: BorderWidthOption;
  /**
   * Controls panel top border width.
   */
  borderT?: BorderWidthOption;
  /**
   * Controls panel right border width.
   */
  borderR?: BorderWidthOption;
  /**
   * Controls panel bottom border width.
   */
  borderB?: BorderWidthOption;
  /**
   * Controls panel left border width.
   */
  borderL?: BorderWidthOption;
  /**
   * Controls panel right and left border width.
   */
  borderX?: BorderWidthOption;
  /**
   * Controls panel top and bottom border width.
   */
  borderY?: BorderWidthOption;

  /**
   * Controls panel border color.
   * @default gray-500
   */
  borderColor?: PaletteColorOption;
  /**
   * Controls panel background color.
   * @default white
   */
  backgroundColor?: PaletteColorOption;

  /**
   * Controls showing shadow behind panel element.
   * @default false
   */
  shadow?: boolean;

  /**
   * Controls all corners rounding
   * @default md
   */
  rounded?: RoundedOption;
  /**
   * Controls top corners rounding
   */
  roundedT?: RoundedOption;
  /**
   * Controls right corners rounding
   */
  roundedR?: RoundedOption;
  /**
   * Controls bottom corners rounding
   */
  roundedB?: RoundedOption;
  /**
   * Controls left corners rounding
   */
  roundedL?: RoundedOption;
  /**
   * Controls top right corner rounding
   */
  roundedTR?: RoundedOption;
  /**
   * Controls bottom right corner rounding
   */
  roundedBR?: RoundedOption;
  /**
   * Controls bottom left corner rounding
   */
  roundedBL?: RoundedOption;
  /**
   * Controls top left corner rounding
   */
  roundedTL?: RoundedOption;

  /**
   * Controls padding of panel
   * @default md
   */
  padding?: PaddingOption;
  /**
   * Controls top padding of panel
   */
  paddingT?: PaddingOption;
  /**
   * Controls right padding of panel
   */
  paddingR?: PaddingOption;
  /**
   * Controls bottom padding of panel
   */
  paddingB?: PaddingOption;
  /**
   * Controls left padding of panel
   */
  paddingL?: PaddingOption;
  /**
   * Controls right and left padding of panel
   */
  paddingX?: PaddingOption;
  /**
   * Controls top and bottom padding of panel
   */
  paddingY?: PaddingOption;
}

export const Panel: FC<PanelProps> = memo((props) => {
  const defaultProps = useContext(DefaultPropsContext);
  const { border: borderDefaultProps, rounded: roundedDefaultProps } = defaultProps?.panelDefaultProps ?? {};

  const defaultBorder = !(
    props.borderT ||
    props.borderR ||
    props.borderB ||
    props.borderL ||
    props.borderX ||
    props.borderY
  )
    ? borderDefaultProps ?? "1"
    : undefined;

  const defaultRounded = !(
    props.roundedT ||
    props.roundedR ||
    props.roundedB ||
    props.roundedL ||
    props.roundedTR ||
    props.roundedBR ||
    props.roundedBL ||
    props.roundedTL
  )
    ? roundedDefaultProps ?? "md"
    : undefined;

  const defaultPadding = !(
    props.paddingT ||
    props.paddingR ||
    props.paddingB ||
    props.paddingL ||
    props.paddingX ||
    props.paddingY
  )
    ? "md"
    : undefined;

  const {
    id,
    children,
    className: classNameProp,
    border = defaultBorder,
    borderT,
    borderR,
    borderB,
    borderL,
    borderX,
    borderY,
    borderColor = "gray-500",
    backgroundColor = "white",
    shadow = false,
    rounded = defaultRounded,
    roundedT,
    roundedR,
    roundedB,
    roundedL,
    roundedTR,
    roundedBR,
    roundedBL,
    roundedTL,
    padding = defaultPadding,
    paddingT,
    paddingR,
    paddingB,
    paddingL,
    paddingX,
    paddingY,
    ...restProps
  } = props;

  const className = classNames(
    "panel",
    {
      // border width top
      "border-t-1": (borderT || borderY || border) === "1",
      "border-t-2": (borderT || borderY || border) === "2",
      "border-t-3": (borderT || borderY || border) === "3",
      // border width right
      "border-r-1": (borderR || borderX || border) === "1",
      "border-r-2": (borderR || borderX || border) === "2",
      "border-r-3": (borderR || borderX || border) === "3",
      // border width bottom
      "border-b-1": (borderB || borderY || border) === "1",
      "border-b-2": (borderB || borderY || border) === "2",
      "border-b-3": (borderB || borderY || border) === "3",
      // border width left
      "border-l-1": (borderL || borderX || border) === "1",
      "border-l-2": (borderL || borderX || border) === "2",
      "border-l-3": (borderL || borderX || border) === "3",
      // border color
      "border-primary-50": borderColor === "primary-50",
      "border-primary-100": borderColor === "primary-100",
      "border-primary-200": borderColor === "primary-200",
      "border-primary-300": borderColor === "primary-300",
      "border-primary-400": borderColor === "primary-400",
      "border-primary-500": borderColor === "primary-500",
      "border-primary-600": borderColor === "primary-600",
      "border-primary-700": borderColor === "primary-700",
      "border-primary-800": borderColor === "primary-800",
      "border-primary-900": borderColor === "primary-900",
      "border-secondary-50": borderColor === "secondary-50",
      "border-secondary-100": borderColor === "secondary-100",
      "border-secondary-200": borderColor === "secondary-200",
      "border-secondary-300": borderColor === "secondary-300",
      "border-secondary-400": borderColor === "secondary-400",
      "border-secondary-500": borderColor === "secondary-500",
      "border-secondary-600": borderColor === "secondary-600",
      "border-secondary-700": borderColor === "secondary-700",
      "border-secondary-800": borderColor === "secondary-800",
      "border-secondary-900": borderColor === "secondary-900",
      "border-interactive-50": borderColor === "interactive-50",
      "border-interactive-100": borderColor === "interactive-100",
      "border-interactive-200": borderColor === "interactive-200",
      "border-interactive-300": borderColor === "interactive-300",
      "border-interactive-400": borderColor === "interactive-400",
      "border-interactive-500": borderColor === "interactive-500",
      "border-interactive-600": borderColor === "interactive-600",
      "border-interactive-700": borderColor === "interactive-700",
      "border-interactive-800": borderColor === "interactive-800",
      "border-interactive-900": borderColor === "interactive-900",
      "border-accent-50": borderColor === "accent-50",
      "border-accent-100": borderColor === "accent-100",
      "border-accent-200": borderColor === "accent-200",
      "border-accent-300": borderColor === "accent-300",
      "border-accent-400": borderColor === "accent-400",
      "border-accent-500": borderColor === "accent-500",
      "border-accent-600": borderColor === "accent-600",
      "border-accent-700": borderColor === "accent-700",
      "border-accent-800": borderColor === "accent-800",
      "border-accent-900": borderColor === "accent-900",
      "border-gray-50": borderColor === "gray-50",
      "border-gray-100": borderColor === "gray-100",
      "border-gray-200": borderColor === "gray-200",
      "border-gray-300": borderColor === "gray-300",
      "border-gray-400": borderColor === "gray-400",
      "border-gray-500": borderColor === "gray-500",
      "border-gray-600": borderColor === "gray-600",
      "border-gray-700": borderColor === "gray-700",
      "border-gray-800": borderColor === "gray-800",
      "border-gray-900": borderColor === "gray-900",
      "border-error-50": borderColor === "error-50",
      "border-error-100": borderColor === "error-100",
      "border-error-200": borderColor === "error-200",
      "border-error-300": borderColor === "error-300",
      "border-error-400": borderColor === "error-400",
      "border-error-500": borderColor === "error-500",
      "border-error-600": borderColor === "error-600",
      "border-error-700": borderColor === "error-700",
      "border-error-800": borderColor === "error-800",
      "border-error-900": borderColor === "error-900",
      "border-warning-50": borderColor === "warning-50",
      "border-warning-100": borderColor === "warning-100",
      "border-warning-200": borderColor === "warning-200",
      "border-warning-300": borderColor === "warning-300",
      "border-warning-400": borderColor === "warning-400",
      "border-warning-500": borderColor === "warning-500",
      "border-warning-600": borderColor === "warning-600",
      "border-warning-700": borderColor === "warning-700",
      "border-warning-800": borderColor === "warning-800",
      "border-warning-900": borderColor === "warning-900",
      "border-success-50": borderColor === "success-50",
      "border-success-100": borderColor === "success-100",
      "border-success-200": borderColor === "success-200",
      "border-success-300": borderColor === "success-300",
      "border-success-400": borderColor === "success-400",
      "border-success-500": borderColor === "success-500",
      "border-success-600": borderColor === "success-600",
      "border-success-700": borderColor === "success-700",
      "border-success-800": borderColor === "success-800",
      "border-success-900": borderColor === "success-900",
      "border-white": borderColor === "white",
      "border-black": borderColor === "black",
      // background color
      "bg-primary-50": backgroundColor === "primary-50",
      "bg-primary-100": backgroundColor === "primary-100",
      "bg-primary-200": backgroundColor === "primary-200",
      "bg-primary-300": backgroundColor === "primary-300",
      "bg-primary-400": backgroundColor === "primary-400",
      "bg-primary-500": backgroundColor === "primary-500",
      "bg-primary-600": backgroundColor === "primary-600",
      "bg-primary-700": backgroundColor === "primary-700",
      "bg-primary-800": backgroundColor === "primary-800",
      "bg-primary-900": backgroundColor === "primary-900",
      "bg-secondary-50": backgroundColor === "secondary-50",
      "bg-secondary-100": backgroundColor === "secondary-100",
      "bg-secondary-200": backgroundColor === "secondary-200",
      "bg-secondary-300": backgroundColor === "secondary-300",
      "bg-secondary-400": backgroundColor === "secondary-400",
      "bg-secondary-500": backgroundColor === "secondary-500",
      "bg-secondary-600": backgroundColor === "secondary-600",
      "bg-secondary-700": backgroundColor === "secondary-700",
      "bg-secondary-800": backgroundColor === "secondary-800",
      "bg-secondary-900": backgroundColor === "secondary-900",
      "bg-interactive-50": backgroundColor === "interactive-50",
      "bg-interactive-100": backgroundColor === "interactive-100",
      "bg-interactive-200": backgroundColor === "interactive-200",
      "bg-interactive-300": backgroundColor === "interactive-300",
      "bg-interactive-400": backgroundColor === "interactive-400",
      "bg-interactive-500": backgroundColor === "interactive-500",
      "bg-interactive-600": backgroundColor === "interactive-600",
      "bg-interactive-700": backgroundColor === "interactive-700",
      "bg-interactive-800": backgroundColor === "interactive-800",
      "bg-interactive-900": backgroundColor === "interactive-900",
      "bg-accent-50": backgroundColor === "accent-50",
      "bg-accent-100": backgroundColor === "accent-100",
      "bg-accent-200": backgroundColor === "accent-200",
      "bg-accent-300": backgroundColor === "accent-300",
      "bg-accent-400": backgroundColor === "accent-400",
      "bg-accent-500": backgroundColor === "accent-500",
      "bg-accent-600": backgroundColor === "accent-600",
      "bg-accent-700": backgroundColor === "accent-700",
      "bg-accent-800": backgroundColor === "accent-800",
      "bg-accent-900": backgroundColor === "accent-900",
      "bg-gray-50": backgroundColor === "gray-50",
      "bg-gray-100": backgroundColor === "gray-100",
      "bg-gray-200": backgroundColor === "gray-200",
      "bg-gray-300": backgroundColor === "gray-300",
      "bg-gray-400": backgroundColor === "gray-400",
      "bg-gray-500": backgroundColor === "gray-500",
      "bg-gray-600": backgroundColor === "gray-600",
      "bg-gray-700": backgroundColor === "gray-700",
      "bg-gray-800": backgroundColor === "gray-800",
      "bg-gray-900": backgroundColor === "gray-900",
      "bg-error-50": backgroundColor === "error-50",
      "bg-error-100": backgroundColor === "error-100",
      "bg-error-200": backgroundColor === "error-200",
      "bg-error-300": backgroundColor === "error-300",
      "bg-error-400": backgroundColor === "error-400",
      "bg-error-500": backgroundColor === "error-500",
      "bg-error-600": backgroundColor === "error-600",
      "bg-error-700": backgroundColor === "error-700",
      "bg-error-800": backgroundColor === "error-800",
      "bg-error-900": backgroundColor === "error-900",
      "bg-warning-50": backgroundColor === "warning-50",
      "bg-warning-100": backgroundColor === "warning-100",
      "bg-warning-200": backgroundColor === "warning-200",
      "bg-warning-300": backgroundColor === "warning-300",
      "bg-warning-400": backgroundColor === "warning-400",
      "bg-warning-500": backgroundColor === "warning-500",
      "bg-warning-600": backgroundColor === "warning-600",
      "bg-warning-700": backgroundColor === "warning-700",
      "bg-warning-800": backgroundColor === "warning-800",
      "bg-warning-900": backgroundColor === "warning-900",
      "bg-success-50": backgroundColor === "success-50",
      "bg-success-100": backgroundColor === "success-100",
      "bg-success-200": backgroundColor === "success-200",
      "bg-success-300": backgroundColor === "success-300",
      "bg-success-400": backgroundColor === "success-400",
      "bg-success-500": backgroundColor === "success-500",
      "bg-success-600": backgroundColor === "success-600",
      "bg-success-700": backgroundColor === "success-700",
      "bg-success-800": backgroundColor === "success-800",
      "bg-success-900": backgroundColor === "success-900",
      "bg-white": backgroundColor === "white",
      "bg-black": backgroundColor === "black",
      // shadow
      "panel-shadow": shadow,
      // border round top-right corner
      "rounded-tr-1": (roundedTR || roundedT || roundedR || rounded) === "xs",
      "rounded-tr-2": (roundedTR || roundedT || roundedR || rounded) === "sm",
      "rounded-tr-3": (roundedTR || roundedT || roundedR || rounded) === "md",
      "rounded-tr-4": (roundedTR || roundedT || roundedR || rounded) === "lg",
      "rounded-tr-5": (roundedTR || roundedT || roundedR || rounded) === "xl",
      // border round bottom-right corner
      "rounded-br-1": (roundedBR || roundedB || roundedR || rounded) === "xs",
      "rounded-br-2": (roundedBR || roundedB || roundedR || rounded) === "sm",
      "rounded-br-3": (roundedBR || roundedB || roundedR || rounded) === "md",
      "rounded-br-4": (roundedBR || roundedB || roundedR || rounded) === "lg",
      "rounded-br-5": (roundedBR || roundedB || roundedR || rounded) === "xl",
      // border round bottom-left corner
      "rounded-bl-1": (roundedBL || roundedB || roundedL || rounded) === "xs",
      "rounded-bl-2": (roundedBL || roundedB || roundedL || rounded) === "sm",
      "rounded-bl-3": (roundedBL || roundedB || roundedL || rounded) === "md",
      "rounded-bl-4": (roundedBL || roundedB || roundedL || rounded) === "lg",
      "rounded-bl-5": (roundedBL || roundedB || roundedL || rounded) === "xl",
      // border round top-left corner
      "rounded-tl-1": (roundedTL || roundedT || roundedL || rounded) === "xs",
      "rounded-tl-2": (roundedTL || roundedT || roundedL || rounded) === "sm",
      "rounded-tl-3": (roundedTL || roundedT || roundedL || rounded) === "md",
      "rounded-tl-4": (roundedTL || roundedT || roundedL || rounded) === "lg",
      "rounded-tl-5": (roundedTL || roundedT || roundedL || rounded) === "xl",

      // padding top
      "pt-1": (paddingT || paddingY || padding) === "xs",
      "pt-2": (paddingT || paddingY || padding) === "sm",
      "pt-3": (paddingT || paddingY || padding) === "md",
      "pt-4": (paddingT || paddingY || padding) === "lg",
      "pt-5": (paddingT || paddingY || padding) === "xl",
      // padding right
      "pr-1": (paddingR || paddingX || padding) === "xs",
      "pr-2": (paddingR || paddingX || padding) === "sm",
      "pr-3": (paddingR || paddingX || padding) === "md",
      "pr-4": (paddingR || paddingX || padding) === "lg",
      "pr-5": (paddingR || paddingX || padding) === "xl",
      // padding bottom
      "pb-1": (paddingB || paddingY || padding) === "xs",
      "pb-2": (paddingB || paddingY || padding) === "sm",
      "pb-3": (paddingB || paddingY || padding) === "md",
      "pb-4": (paddingB || paddingY || padding) === "lg",
      "pb-5": (paddingB || paddingY || padding) === "xl",
      // padding left
      "pl-1": (paddingL || paddingX || padding) === "xs",
      "pl-2": (paddingL || paddingX || padding) === "sm",
      "pl-3": (paddingL || paddingX || padding) === "md",
      "pl-4": (paddingL || paddingX || padding) === "lg",
      "pl-5": (paddingL || paddingX || padding) === "xl",
    },
    classNameProp
  );

  return (
    <div id={id} className={className} {...restProps}>
      {children}
    </div>
  );
});
Panel.displayName = "Panel";
